import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, UploadCloud, FileText, Send, Loader2, LogOut, Download, ListChecks, Clock, ExternalLink,
} from "lucide-react";
import { LOGO_URL, BRAND, STATUS_COLORS, BUREAU_COLORS } from "@/lib/brand";
import { api, getToken, API, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const BUREAUS = ["Equifax", "Experian", "TransUnion", "Furnisher"];

export default function Portal() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [req, setReq] = useState({ bureau: "Equifax", creditor: "", accountNumber: "", amount: "", reason: "" });

  const load = () => api.get("/portal/overview").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      await api.post("/portal/reports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const submitReq = async () => {
    setSaving(true);
    try {
      await api.post("/portal/dispute-requests", req);
      toast.success("Request submitted for review");
      setOpen(false); setReq({ bureau: "Equifax", creditor: "", accountNumber: "", amount: "", reason: "" }); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const fulfill = async (id, e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      await api.post(`/portal/doc-requests/${id}/fulfill`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded — thank you"); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };

  const decide = async (id, decision) => {
    try { await api.post(`/portal/tradelines/${id}/decision`, { decision }); toast.success(decision === "fraud" ? "Marked as fraud" : "Marked as yours"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  if (!data) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-7 w-7 animate-spin text-[#C1121F]" /></div>;

  const c = data.consumer || {};

  return (
    <div className="min-h-screen" data-testid="portal-page">
      <header className="h-20 border-b border-neutral-200 glass sticky top-0 z-20 flex items-center justify-between px-4 sm:px-8">
        <div className="flex items-center gap-3">
          <span className="bg-white rounded-lg p-1.5"><img src={LOGO_URL} alt={BRAND} className="h-9 w-auto object-contain" /></span>
          <div>
            <div className="font-display font-extrabold text-sm">{BRAND}</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#C1121F]">Client Portal</div>
          </div>
        </div>
        <Button onClick={doLogout} variant="outline" className="border-neutral-200 bg-neutral-50 hover:bg-neutral-200" data-testid="portal-logout-button">
          <LogOut className="h-4 w-4 mr-2" /> Sign Out
        </Button>
      </header>

      <div className="max-w-5xl mx-auto p-4 sm:p-8 space-y-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#C1121F]/40 bg-[#C1121F]/10 px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-[#9B0E18]">
              <ShieldCheck className="h-3.5 w-3.5" /> Welcome, {user?.name}
            </div>
            <h1 className="mt-3 font-display text-2xl font-extrabold">Your identity-theft case</h1>
            <p className="text-sm text-neutral-500 mt-1">FTC Report: {c.ftcReportId || "—"} · Status: {c.status || "—"}</p>
          </div>
        </motion.div>

        {/* Get your credit reports */}
        <section data-testid="portal-resources-section">
          <div className="flex items-center gap-2 mb-2"><Download className="h-5 w-5 text-[#C1121F]" /><h2 className="font-display text-lg font-bold">Get your credit reports</h2></div>
          <p className="text-sm text-neutral-500 mb-4">Pull your reports and file your identity-theft report using these official resources, then upload them below so we can start disputing.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { href: "https://www.annualcreditreport.com", title: "AnnualCreditReport.com", desc: "Your free official reports from all three bureaus." },
              { href: "https://www.creditkarma.com", title: "Credit Karma", desc: "Free credit scores and ongoing report monitoring." },
              { href: "https://www.identitytheft.gov", title: "IdentityTheft.gov", desc: "File your FTC Identity Theft Report (required for a §605B block)." },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-xl border border-neutral-200 bg-[#FFFFFF]/80 p-4 hover:border-[#C1121F]/40 hover:shadow-lg hover:shadow-red-900/5 hover:-translate-y-0.5 transition-[border-color,transform,box-shadow] duration-200"
                data-testid={`portal-resource-${l.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display font-bold text-neutral-900">{l.title}</span>
                  <ExternalLink className="h-4 w-4 text-neutral-400 group-hover:text-[#C1121F] transition-colors" />
                </div>
                <p className="mt-1.5 text-xs text-neutral-500 leading-relaxed">{l.desc}</p>
              </a>
            ))}
          </div>
        </section>

        {/* Disputes */}
        <section>
          <div className="flex items-center gap-2 mb-4"><ListChecks className="h-5 w-5 text-[#C1121F]" /><h2 className="font-display text-lg font-bold">Dispute status</h2></div>
          <div className="space-y-3">
            {data.disputes.length === 0 && <p className="text-sm text-neutral-400">No disputes yet. Your advocate will file them soon.</p>}
            {data.disputes.map((d) => {
              const bc = BUREAU_COLORS[d.bureau] || BUREAU_COLORS.Furnisher;
              return (
                <div key={d.id} className="rounded-xl border border-neutral-200 bg-[#FFFFFF]/80 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="portal-dispute-row">
                  <div>
                    <div className="font-medium">{d.creditor || d.disputeType}</div>
                    <div className="text-xs text-neutral-400 flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-md border ${bc.chip} ${bc.text} ${bc.border}`}>{d.bureau}</span>
                      <span className="font-mono">{d.accountNumber}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[#9B0E18]">{d.amount}</span>
                    <span className={`text-xs px-2.5 py-1 rounded-md border ${STATUS_COLORS[d.status] || "bg-stone-100 text-stone-600 border-stone-300"}`}>{d.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Account review */}
        {(data.tradelines || []).length > 0 && (
          <section data-testid="portal-review-section">
            <div className="flex items-center gap-2 mb-2"><ShieldCheck className="h-5 w-5 text-[#C1121F]" /><h2 className="font-display text-lg font-bold">Review the accounts we found</h2></div>
            <p className="text-sm text-neutral-500 mb-4">Tell us which accounts are yours and which are fraud. Anything you flag as fraud will be disputed with the bureaus.</p>
            <div className="space-y-3">
              {data.tradelines.map((t) => {
                const bc = BUREAU_COLORS[t.bureau] || BUREAU_COLORS.Furnisher;
                return (
                  <div key={t.id} className="rounded-xl border border-neutral-200 bg-[#FFFFFF]/80 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="portal-tradeline-row">
                    <div>
                      <div className="font-medium">{t.creditor}</div>
                      <div className="text-xs text-neutral-400 flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded-md border ${bc.chip} ${bc.text} ${bc.border}`}>{t.bureau}</span>
                        <span>{t.negativeType}</span><span className="font-mono text-[#9B0E18]">{t.balance}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => decide(t.id, "fraud")} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${t.clientDecision === "fraud" ? "bg-rose-500 text-white" : "bg-neutral-50 border border-neutral-200 hover:bg-neutral-200 text-rose-600"}`} data-testid="tradeline-fraud-button">This is fraud</button>
                      <button onClick={() => decide(t.id, "mine")} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${t.clientDecision === "mine" ? "bg-emerald-500/20 border border-emerald-500/40 text-emerald-700" : "bg-neutral-50 border border-neutral-200 hover:bg-neutral-200 text-neutral-600"}`} data-testid="tradeline-mine-button">This is mine</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Requested documents */}
        {(data.docRequests || []).length > 0 && (
          <section data-testid="portal-doc-requests">
            <div className="flex items-center gap-2 mb-4"><UploadCloud className="h-5 w-5 text-[#C1121F]" /><h2 className="font-display text-lg font-bold">Documents your advocate requested</h2></div>
            <div className="space-y-3">
              {data.docRequests.map((r) => (
                <div key={r.id} className="rounded-xl border border-[#C1121F]/30 bg-[#C1121F]/[0.06] p-4 flex flex-wrap items-center justify-between gap-3" data-testid="portal-doc-request-item">
                  <div>
                    <div className="font-medium text-[#9B0E18]">{r.label}</div>
                    {r.note && <div className="text-xs text-neutral-500">{r.note}</div>}
                  </div>
                  <label className="cursor-pointer">
                    <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.csv" onChange={(e) => fulfill(r.id, e)} data-testid="portal-fulfill-input" />
                    <span className="inline-flex items-center gap-2 rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold px-4 py-2 text-sm">
                      <UploadCloud className="h-4 w-4" /> Upload
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Documents */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#C1121F]" /><h2 className="font-display text-lg font-bold">My documents</h2></div>
            <label className="cursor-pointer">
              <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.csv" onChange={upload} data-testid="portal-upload-input" />
              <span className="inline-flex items-center gap-2 rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold px-4 py-2 text-sm">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Upload
              </span>
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.reports.length === 0 && <p className="text-sm text-neutral-400">No documents uploaded yet.</p>}
            {data.reports.map((r) => (
              <div key={r.id} className="rounded-xl border border-neutral-200 bg-[#FFFFFF]/80 p-4 flex items-center gap-3" data-testid="portal-report-item">
                <FileText className="h-5 w-5 text-neutral-500" />
                <div className="min-w-0"><div className="text-sm truncate">{r.original_filename}</div><div className="text-xs text-neutral-400">{r.uploaded_by === "consumer" ? "Uploaded by you" : "On file"}</div></div>
              </div>
            ))}
          </div>
        </section>

        {/* Requests */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><Send className="h-5 w-5 text-[#C1121F]" /><h2 className="font-display text-lg font-bold">My dispute requests</h2></div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-neutral-50 border border-neutral-200 hover:bg-neutral-200 text-neutral-900" data-testid="portal-new-request-button">New Request</Button>
              </DialogTrigger>
              <DialogContent className="bg-[#F6EEEE] border-neutral-200">
                <DialogHeader><DialogTitle className="font-display">Request a new dispute</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label className="text-neutral-600">Bureau</Label>
                    <Select value={req.bureau} onValueChange={(v) => setReq({ ...req, bureau: v })}>
                      <SelectTrigger className="mt-1.5 bg-[#FFFFFF] border-neutral-200" data-testid="portal-request-bureau"><SelectValue /></SelectTrigger>
                      <SelectContent>{BUREAUS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-neutral-600">Creditor / account name</Label><Input value={req.creditor} onChange={(e) => setReq({ ...req, creditor: e.target.value })} className="mt-1.5 bg-[#FFFFFF] border-neutral-200" data-testid="portal-request-creditor" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-neutral-600">Account #</Label><Input value={req.accountNumber} onChange={(e) => setReq({ ...req, accountNumber: e.target.value })} className="mt-1.5 bg-[#FFFFFF] border-neutral-200" /></div>
                    <div><Label className="text-neutral-600">Amount</Label><Input value={req.amount} onChange={(e) => setReq({ ...req, amount: e.target.value })} placeholder="$5,000" className="mt-1.5 bg-[#FFFFFF] border-neutral-200" /></div>
                  </div>
                  <div><Label className="text-neutral-600">Why is this fraudulent?</Label><Textarea value={req.reason} onChange={(e) => setReq({ ...req, reason: e.target.value })} className="mt-1.5 bg-[#FFFFFF] border-neutral-200" data-testid="portal-request-reason" /></div>
                </div>
                <DialogFooter>
                  <Button onClick={submitReq} disabled={saving} className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="portal-submit-request">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for review"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="space-y-3">
            {data.requests.length === 0 && <p className="text-sm text-neutral-400">No requests yet.</p>}
            {data.requests.map((r) => (
              <div key={r.id} className="rounded-xl border border-neutral-200 bg-[#FFFFFF]/80 p-4 flex items-center justify-between gap-3" data-testid="portal-request-item">
                <div><div className="text-sm font-medium">{r.creditor || "Dispute request"} · {r.bureau}</div><div className="text-xs text-neutral-400">{r.reason}</div></div>
                <span className="text-xs px-2.5 py-1 rounded-md border bg-amber-50 text-amber-700 border-amber-200 flex items-center gap-1"><Clock className="h-3 w-3" />{r.status}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
